import React, { useState, useEffect } from 'react';
import SiteScreener from './components/SiteScreener';
import ProjectsPage from './components/ProjectsPage';
import ProjectDetail from './components/ProjectDetail';
import ProjectsV2 from './components/projects/ProjectsV2';
import { ProjectsProvider, useProjects } from './contexts/ProjectsContext';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { TeamProvider, useTeam } from './contexts/TeamContext';
import { PremiumProvider, useIsPremium } from './contexts/PremiumContext';
import { RegionLockProvider, useRegionLock, DEFAULT_FREE_REGION, REGION_BBOXES } from './contexts/RegionLockContext';
import { OrgProvider, useOrg } from './contexts/OrgContext';
import { openCalendly } from './utils/calendly';
import { tenantCountry } from './utils/tenantScope';
import './components/PremiumLock/PremiumLock.css';
// NotificationBell import removed — bell hidden from header until we
// actually have a notification source. Component file kept for revival.
import { MapErrorBoundary } from './components/MapErrorBoundary';
import FeedbackWidget, { openFeedback } from './components/FeedbackWidget';
import AdminOrgs from './components/AdminOrgs';
import LoginRefusal, { RETRY_LOGIN } from './components/LoginRefusal';
import './App.css';
import { useTranslation } from './translations';

// Auth0 config from env
const AUTH0_DOMAIN = import.meta.env?.VITE_AUTH0_DOMAIN || '';
const AUTH0_CLIENT_ID = import.meta.env?.VITE_AUTH0_CLIENT_ID || '';
const AUTH0_AUDIENCE = import.meta.env?.VITE_AUTH0_AUDIENCE || '';
// Single-org pilot: pass org_id in authorizationParams so Auth0 skips the
// "Identify Organization" prompt (which wants a domain-mapped email and
// fails for our database connection). When we go multi-org, drop this
// and switch Auth0 LandFalken → Login Flow back to "Prompt for Organization".
const AUTH0_ORG_ID = import.meta.env?.VITE_AUTH0_ORG_ID || '';
const AUTH0_CONFIGURED = AUTH0_DOMAIN && !AUTH0_DOMAIN.includes('your-tenant');

// Header link shown only to non-premium users. Sits beside the
// Sign Out / user button. Premium users render nothing in this slot.
function PremiumHeaderLink() {
  const { isPremium, loading } = useIsPremium();
  const { user } = useAuth0();
  if (loading || isPremium) return null;
  return (
    <button
      type="button"
      className="premium-header-link"
      onClick={() => openCalendly(user)}
    >
      Premium <span aria-hidden="true">›</span>
    </button>
  );
}

// Auto-redirect bare /app to /app?test_region=stavanger for non-premium
// users. Approved unilaterally in the Implementation Brief response:
// a free user landing on bare /app should always have the region lock
// explicit in the URL. Premium users are untouched. Runs once after
// premium status loads — if the URL already has a test_region param,
// no redirect.
function RegionAutoRedirect() {
  const { isPremium, loading } = useIsPremium();
  useEffect(() => {
    if (loading) return;
    if (isPremium) return;
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('test_region')) return;
      qs.set('test_region', DEFAULT_FREE_REGION);
      const url = `${window.location.pathname}?${qs.toString()}${window.location.hash}`;
      window.history.replaceState(null, '', url);
    } catch { /* SSR / sandboxed */ }
  }, [isPremium, loading]);
  return null;
}

function ProjectBadge() {
  const { projectCount, enrichingCount } = useProjects();
  if (!projectCount) return null;
  return (
    <span className={`nav-badge ${enrichingCount > 0 ? 'nav-badge-enriching' : ''}`}>
      {projectCount}{enrichingCount > 0 ? '...' : ''}
    </span>
  );
}

// Caption under the signed-in email: why this session is (not) premium.
const TIER_CAPTION = { org: 'Premium · organisation', user: 'Premium · subscription', none: 'Free tier' };

function AuthButton() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } = useAuth0();
  const { isPremium, premiumSource, loading: premiumLoading } = useIsPremium();

  if (isLoading) return <span className="auth-loading">...</span>;

  if (isAuthenticated) {
    const caption = premiumLoading ? null : (isPremium ? (TIER_CAPTION[premiumSource] || TIER_CAPTION.user) : TIER_CAPTION.none);
    return (
      <div className="auth-user">
        <span className="auth-email" style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.15 }}>
          {user?.name || user?.email}
          {caption && <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }} title="How this session's tier was decided (see docs/premium-flow.md)">{caption}</span>}
        </span>
        <button className="btn-slim btn-signout"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <button className="btn-slim btn-signin"
            onClick={() => loginWithRedirect()}>
      Sign In
    </button>
  );
}

function FallbackAuthButton() {
  // Sprint F · Mock auth — picks a hardcoded team member as the
  // current user. Replaces the disabled "Sign In" placeholder until
  // real Auth0 credentials are wired up. The signed-in user persists
  // to localStorage via TeamContext so refreshes keep the identity.
  const { team, currentUser, signIn, signOut } = useTeam();
  const [open, setOpen] = useState(false);

  if (currentUser) {
    return (
      <div className="team-pill" onClick={() => setOpen(o => !o)}>
        <span className="team-pill-avatar" style={{ background: currentUser.color }}>
          {currentUser.initials}
        </span>
        <span className="team-pill-name">{currentUser.name}</span>
        <span className="team-pill-caret">▾</span>
        {open && (
          <div className="team-menu" onClick={e => e.stopPropagation()}>
            <div className="team-menu-section">Switch user</div>
            {team.filter(u => u.id !== currentUser.id).map(u => (
              <button key={u.id} className="team-menu-item"
                      onClick={() => { signIn(u.id); setOpen(false); }}>
                <span className="team-pill-avatar" style={{ background: u.color }}>{u.initials}</span>
                {u.name}
              </button>
            ))}
            <div className="team-menu-divider" />
            <button className="team-menu-item team-menu-signout"
                    onClick={() => { signOut(); setOpen(false); }}>
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="team-pill" onClick={() => setOpen(o => !o)}>
      <span className="team-pill-name">Sign in</span>
      <span className="team-pill-caret">▾</span>
      {open && (
        <div className="team-menu" onClick={e => e.stopPropagation()}>
          <div className="team-menu-section">Pick a team member</div>
          {team.map(u => (
            <button key={u.id} className="team-menu-item"
                    onClick={() => { signIn(u.id); setOpen(false); }}>
              <span className="team-pill-avatar" style={{ background: u.color }}>{u.initials}</span>
              {u.name}
              <span className="team-menu-email">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Login gate — requires authentication to access /app
function LoginGate({ children }) {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const { org } = useOrg();
  
  if (isLoading) {
    return (
      <div className="login-gate">
        <div className="login-gate-content">
          <div className="login-gate-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="login-gate">
        <div className="login-gate-content">
          <h1 className="login-gate-logo">SEIDR</h1>
          <p className="login-gate-tagline">Site screening for renewable energy</p>
          <LoginRefusal error={error} orgName={org?.display_name} />
          <button 
            className="login-gate-btn"
            onClick={() => loginWithRedirect(error ? RETRY_LOGIN : undefined)}
          >
            {error ? 'Sign in with another account' : 'Sign in to continue'}
          </button>
          <a href="/" className="login-gate-link">← Back to homepage</a>
        </div>
      </div>
    );
  }
  
  return children;
}

function AppContent() {
  // Locale defaults to English everywhere except the Italia tenant,
  // where it defaults to Italian (with an EN switcher in the header —
  // see LanguageSwitcher below). The NO/DE/FR translations are stale
  // (no translator pipeline for those), which is why only 'it' gets a
  // real default-on treatment here. useTranslation falls back to the
  // English string for any key missing in a given language, so a
  // partial 'it' translation set is safe.
  // Each tenant searches one country (utils/tenantScope.js): its
  // default_country when offered, else the first of branding.countries,
  // else Norway. Subdomain tenants (e.g. italia.seidr.makers.bot) boot the
  // map on that country. See backend/app/api/orgs.py OrgBranding.
  const { branding } = useOrg();
  const initialCountry = tenantCountry(branding);
  const initialLanguage = branding?.default_country === 'IT' ? 'it' : 'en';
  const [language, setLanguage] = useState(initialLanguage);
  const t = useTranslation(language);

  const [route, setRoute] = useState(() => window.location.hash);

  React.useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Route matching.
  //
  // NEW (projects-map-view PR): #/projects and #/projects/{siteId} render
  // ProjectsV2 (map + selected-site card + tabs). Legacy list + detail live
  // at #/projects-legacy for parity checking; removed in the next PR.
  const isProjectsLegacy = route === '#/projects-legacy' || route.startsWith('#/projects-legacy?');
  const legacyDetailMatch = route.match(/^#\/projects-legacy\/(.+)$/);
  const isLegacyDetail = !!legacyDetailMatch;
  const isProjectsV2 = (
    route === '#/projects' ||
    route.startsWith('#/projects?') ||
    /^#\/projects\/[^/]+/.test(route)
  );
  const isAdminOrgs = route === '#/admin/orgs';
  const isMap = !isProjectsV2 && !isProjectsLegacy && !isLegacyDetail && !isAdminOrgs;
  const needsScroll = isProjectsV2 || isProjectsLegacy || isLegacyDetail || isAdminOrgs;

  return (
    <TeamProvider>
    <PremiumProvider>
    <RegionLockProvider>
    <ProjectsProvider>
      <RegionAutoRedirect />
      <div className="App">
        {/* ── Top bar ──────────────────────────────────────────── */}
        <header className="header-top">
          <div className="header-logo" onClick={() => { window.location.hash = ''; }}>
            <span className="header-logo-text">SEIDR</span>
          </div>
          <nav className="header-nav">
            <a href="#/" className={`nav-link ${isMap ? 'nav-active' : ''}`}>Map</a>
            <a href="#/projects" className={`nav-link ${isProjectsV2 || isProjectsLegacy || isLegacyDetail ? 'nav-active' : ''}`}>
              Projects <ProjectBadge />
            </a>
            {/* Feedback link only on Projects pages — map view has it in the free-tier banner */}
            {!isMap && (
              <button type="button" className="nav-link nav-feedback" onClick={openFeedback}>
                💬 Feedback
              </button>
            )}
          </nav>
          <div className="header-right">
            <PremiumHeaderLink />
            {AUTH0_CONFIGURED ? <AuthButton /> : <FallbackAuthButton />}
            {initialLanguage !== 'en' && (
              <div className="lang-switcher">
                <button
                  type="button"
                  className={`lang-switcher-btn ${language === 'it' ? 'active' : ''}`}
                  onClick={() => setLanguage('it')}
                >
                  IT
                </button>
                <button
                  type="button"
                  className={`lang-switcher-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => setLanguage('en')}
                >
                  EN
                </button>
              </div>
            )}
          </div>
        </header>

        {/* ── Main content ─────────────────────────────────────── */}
        {/* Route-level conditional mount: exactly one <Map> in the DOM at
            once. Leaving #/projects unmounts ProjectsMap; leaving #/
            unmounts SiteScreener. State reset on route change is acceptable
            per Step-0 plan (Q6a). See PR body for map-count verification. */}
        <main className="app-main" style={needsScroll ? { overflow: 'auto' } : undefined}>
          {isLegacyDetail && (
            <ProjectDetail projectId={legacyDetailMatch[1]} />
          )}
          {isAdminOrgs && <AdminOrgs />}
          {/* Route-level conditional mount so exactly one <Map> lives in
              the DOM at any time. Leaving #/projects unmounts ProjectsMap;
              leaving #/ unmounts SiteScreener. Legacy list stays
              reachable at #/projects-legacy until V2 parity is signed off. */}
          {isProjectsLegacy && (
            <div style={{ overflow: 'auto', height: '100%' }}>
              <ProjectsPage />
            </div>
          )}
          {isProjectsV2 && (
            <div style={{ overflow: 'auto', height: '100%' }}>
              <ProjectsV2 active={isProjectsV2} />
            </div>
          )}
          {/* 2026-10-04: SiteScreener stays MOUNTED across route changes
              and is only hidden while Projects / admin views are shown.
              Unmounting it threw away the whole search session (results,
              map position, an in-flight SSE search) every time the user
              glanced at Projects. display:none keeps React state and
              lets a running search finish in the background; the
              ResizeObserver inside SiteScreener calls map.resize() when
              the container becomes visible again. ProjectsMap is still
              route-mounted, so at most two maps exist at once. */}
          <div
            aria-hidden={!isMap}
            style={isMap
              ? { height: '100%', position: 'relative' }
              // Hidden but still laid out at full size: Mapbox keeps a real
              // canvas (display:none gave it a 0×0 canvas when the app was
              // opened on #/projects, and the map came back blank).
              : { position: 'absolute', inset: 0, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}
          >
            <MapErrorBoundary>
              <SiteScreener language={language} initialCountry={initialCountry} />
            </MapErrorBoundary>
          </div>
        </main>
        {/* Modal only — entry points are the nav link (Projects) or the
            free-tier banner button (Map, see SiteScreener). */}
        <FeedbackWidget showFab={false} />
      </div>
    </ProjectsProvider>
    </RegionLockProvider>
    </PremiumProvider>
    </TeamProvider>
  );
}

// Wraps Auth0Provider once OrgContext has resolved. Per-tenant subdomains
// (e.g. italia.seidr.makers.bot) can have their OWN Auth0 organization —
// orgs.id IS the Auth0 org_id (see backend/app/api/orgs.py create_org,
// which inserts req.auth0_org_id straight into orgs.id). Previously this
// component read a single static VITE_AUTH0_ORG_ID env var, which meant
// every tenant shared one Auth0 org no matter what the admin UI implied.
// Now the `organization` param is resolved from OrgContext at runtime,
// falling back to the static env var if the org fetch fails/returns none
// (e.g. bare seidr.makers.bot with no X-Org-Slug match).
function AuthenticatedApp() {
  const { org, loading: orgLoading } = useOrg();

  // Block on org resolution so Auth0Provider mounts with the right
  // `organization` param from the first render — Auth0's SDK does not
  // support changing `organization` after initial mount without a full
  // re-login, so we can't let it mount early with the wrong value.
  if (orgLoading) {
    return (
      <div className="login-gate">
        <div className="login-gate-content">
          <div className="login-gate-spinner" />
        </div>
      </div>
    );
  }

  const organizationId = org?.id || AUTH0_ORG_ID || undefined;

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        // After login, land inside the product (/app). The product
        // and marketing bundles share one Auth0 tenant, but only
        // /app is the gated experience — marketing pages can also
        // launch loginWithRedirect (see MarketingApp.jsx) and the
        // user funnels to the same /app destination.
        redirect_uri: `${window.location.origin}/app`,
        audience: AUTH0_AUDIENCE || undefined,
        organization: organizationId,
      }}
      // Tokens live in memory only — not in localStorage. Trade-off:
      // XSS can no longer just read the token out of storage and
      // exfiltrate it; the cost is one iframe-based silent-auth
      // round-trip when the user reloads the page (handled by Auth0
      // SDK transparently). Session persists across reload as long
      // as Auth0's session cookie at the IdP is still valid.
      cacheLocation="memory"
    >
      <LoginGate>
        <AppContent />
      </LoginGate>
    </Auth0Provider>
  );
}

function App() {
  // Only wrap with Auth0Provider if configured
  if (AUTH0_CONFIGURED) {
    return (
      <OrgProvider>
        <AuthenticatedApp />
      </OrgProvider>
    );
  }

  // Dev mode without Auth0 — no login gate
  return <OrgProvider><AppContent /></OrgProvider>;
}

export default App;
